import {
  Activity,
  Award,
  Bell,
  Blocks,
  Boxes,
  Bot,
  BookOpen,
  Building2,
  CalendarRange,
  CheckCheck,
  Clock,
  Compass,
  Cpu,
  Database,
  Euro,
  FolderKanban,
  FlaskConical,
  GraduationCap,
  Hammer,
  Home,
  KeyRound,
  LayoutGrid,
  LayoutTemplate,
  LifeBuoy,
  ListChecks,
  MessageSquare,
  Mic,
  Network,
  PanelsTopLeft,
  Plug,
  Rocket,
  ScrollText,
  Settings2,
  ShieldCheck,
  Shuffle,
  SquareKanban,
  Sprout,
  Target,
  TrendingUp,
  Users,
  UsersRound,
  Wallet,
  type LucideIcon,
} from 'lucide-react'

import type { RoleKey } from '@/access/roles'
import { navStateForPath, type NavItemState } from '@/surfaces/registry'

export type { NavItemState }

/**
 * THE NAVIGATION MODEL — unlocked deliberately (Agon's ruling, 2026-09-19,
 * TASK-33343000).
 *
 * This module was "LOCKED — the navigation model" from PX-W6.6 onward: one
 * declared structure per module, shared by every surface, so the rail never
 * drifted between screens. That lock stays in force for HOW a module is
 * declared; what changed is WHAT was declared. Two defects justified opening
 * it: `myNav`/`adminNav` used to CONCATENATE every module's sections into one
 * flat rail (opening `/admin/projects` rendered Administration plus the whole
 * Library and Resources modules — 22 rows in one list), and a rail item's
 * built-or-not state was a hand-set `concept?: boolean` flag that drifted from
 * the surface registry (`@/surfaces/registry.ts`) — five rows pointed at
 * whole-page concept surfaces and carried no flag at all.
 *
 * The fix is structural, not cosmetic: the product is now declared as GROUPS
 * of MODULES, each module a small set of SECTIONS, and every item's `state`
 * is DERIVED from the registry via `navStateForPath` — never hand-set (I-2).
 * `navGroups()` is the level-1 model the shell's module switcher and the Home
 * overview both read; the per-module rail builders (`myNav`, `adminNav`,
 * `libraryNav`, `resourcesNav`, `projectNav`) are UNCHANGED in name and
 * signature — every existing page keeps calling them exactly as before — but
 * `myNav`/`adminNav` no longer concatenate: each returns only its own
 * module's section(s), which is what stops the defect at its root without
 * touching the ~50 pages that pass one of these rails to `<AppShell nav={…}>`.
 *
 * `icon` is what the COLLAPSED rail renders (the label is unavailable there),
 * so every item declares one: an item without an icon disappears into an
 * unlabelled row the moment the rail is unpinned.
 */
export interface NavItem {
  /**
   * The `nav` namespace key the shell renders (TASK-505148A8, batch contract
   * C-5). `label` stays the EN literal: it is the row's identity for tests and
   * React keys, and the `defaultValue` when a key is missing.
   */
  labelKey: string
  label: string
  /** Absent ONLY for a deck screen that has no route (0.3 Roles & permissions). */
  to?: string
  icon?: LucideIcon
  count?: number
  /** Derived from the registry via `navStateForPath` — never hand-set (I-2). */
  state: NavItemState
}

export interface NavSection {
  /**
   * The `nav` namespace key for the heading — ABSENT for a data heading (the
   * engagement's client name in `projectModuleSections`), which is rendered
   * verbatim in every locale.
   */
  headingKey?: string
  heading: string
  items: readonly NavItem[]
}

/**
 * The minimum a project must expose to get a rail. PX-W6.6 narrowed this from
 * the demo seed's `Project` to the two fields the rail actually renders, so
 * `projectNav` composes over the LIVE projects state (`ProjectSummary`) and the
 * navigation model imports nothing from `@/demo` (invariant I-7).
 */
export interface ProjectRailRef {
  /** The `:projectId` route segment. */
  id: string
  /** The rail section heading — the client the engagement belongs to. */
  client: string
}

/** A rail row whose state is ALWAYS derived from the registry — never hand-set. */
const link = (
  labelKey: string,
  label: string,
  to: string,
  icon?: LucideIcon,
  count?: number
): NavItem => ({
  labelKey,
  label,
  to,
  icon,
  count,
  state: navStateForPath(to),
})

/** The one deck screen with no route (0.3 Roles & permissions) — always coming-soon. */
const unrouted = (labelKey: string, label: string, icon?: LucideIcon): NavItem => ({
  labelKey,
  label,
  icon,
  state: navStateForPath(undefined),
})

/* ── PX group — module `home` (deck 0.1 / 0.2) ──────────────────────────── */

const PX_SECTION: NavSection = {
  headingKey: 'section.px',
  heading: 'PX',
  items: [link('item.home', 'Home', '/home', Home), link('item.productMap', 'Product map', '/architecture', Network)],
}

/* ── Resources module (deck 1.1 .. 1.9) ─────────────────────────────────── */

export const resourcesNav: readonly NavSection[] = [
  {
    headingKey: 'section.resources',
    heading: 'Resources',
    items: [
      link('item.peoplePool', 'People pool', '/resources/pool', Users),
      link('item.capacityForecast', 'Capacity forecast', '/resources/capacity', CalendarRange),
      link('item.allocation', 'Allocation', '/resources/allocations', Shuffle),
      link('item.skillsMatrix', 'Skills matrix', '/resources/skills', Target),
      link('item.utilisation', 'Utilisation', '/resources/utilisation', TrendingUp),
      link('item.talentPipeline', 'Talent pipeline', '/resources/pipeline', Sprout),
      link('item.trainingCenter', 'Training center', '/resources/training', GraduationCap),
      link('item.badges', 'Badges', '/resources/badges', Award),
    ],
  },
] as const

/* ── Projects & library module (deck 2.1 .. 2.11) ───────────────────────── */

export const libraryNav: readonly NavSection[] = [
  {
    headingKey: 'section.portfolio',
    heading: 'Portfolio',
    items: [
      link('item.allProjects', 'All projects', '/projects', LayoutGrid),
      link('item.portfolioHealth', 'Portfolio health', '/projects/health', Activity),
    ],
  },
  {
    headingKey: 'section.library',
    heading: 'Library',
    items: [
      link('item.templateLibrary', 'Template library', '/library/templates', LayoutTemplate),
      link('item.requirementsLibrary', 'Requirements library', '/library/requirements', ListChecks),
      link('item.agentLibrary', 'Agent library', '/library/agents', Bot),
      link('item.blueprintConfiguration', 'Blueprint configuration', '/library/blueprints', Blocks),
      link('item.playbookConfiguration', 'Playbook configuration', '/library/playbooks', BookOpen),
    ],
  },
  {
    headingKey: 'section.clients',
    heading: 'Clients',
    items: [
      // PX-W6.2: the REAL client register, above the demo Clients & AMS.
      link('item.clientRegister', 'Client register', '/clients/register', Building2),
      link('item.clientsAndAms', 'Clients & AMS', '/clients', Building2),
    ],
  },
] as const

/**
 * My projects module (deck 3.1 .. 3.9) — one engagement's rail, plus the
 * commercial surfaces and the client portal.
 *
 * ⚠ No counts. PX-W6.6 dropped the seed-derived requirements badge: a rail
 * count sourced from a fixture is a claim about the live engagement that the
 * live engagement never made. The count returns when the requirements register
 * has a backend (W3).
 *
 * `overviewTo` is `/project` (the redirect that resolves the first engagement)
 * when there is no concrete `project` ref — the level-1 "My projects"
 * destination the ticket's own deck-to-rail map names — and the real
 * `/project/{id}` overview otherwise, exactly as `projectNav` rendered it
 * before this ticket.
 */
const projectModuleSections = (project: ProjectRailRef | null): readonly NavSection[] => {
  const ref = project ?? { id: '{id}', client: 'Client' }
  const overviewTo = project === null ? '/project' : `/project/${project.id}`
  return [
    {
      // A DATA heading (the engagement's client name): no `headingKey`, so it
      // renders verbatim in every locale.
      heading: ref.client,
      items: [
        link('item.overview', 'Overview', overviewTo, Compass),
        link('item.interviews', 'Interviews', `/project/${ref.id}/interviews`, Mic),
        link('item.requirements', 'Requirements', `/project/${ref.id}/requirements`, ListChecks),
        link('item.blueprint', 'Blueprint', `/project/${ref.id}/blueprint`, Blocks),
        link('item.buildBoard', 'Build board', `/project/${ref.id}/build`, Hammer),
        link('item.migration', 'Migration', `/project/${ref.id}/migration`, Database),
        link('item.testAndUat', 'Test & UAT', `/project/${ref.id}/test`, FlaskConical),
        link('item.goLivePlan', 'Go-live plan', `/project/${ref.id}/go-live`, Rocket),
        link('item.amsAndUpgrades', 'AMS & upgrades', `/project/${ref.id}/ams`, LifeBuoy),
      ],
    },
    {
      // PX-W6.4: the commercial project surfaces, in their own section below
      // the phase run (they carry no deck screen).
      headingKey: 'section.commercial',
      heading: 'Commercial',
      items: [
        link('item.budget', 'Budget', `/project/${ref.id}/budget`, Wallet),
        link('item.agentSpend', 'Agent spend', `/project/${ref.id}/agent-spend`, Bot),
      ],
    },
    {
      headingKey: 'section.client',
      heading: 'Client',
      items: [link('item.clientPortal', 'Client portal', '/portal', PanelsTopLeft)],
    },
  ]
}

/** A project's rail (screen 3.x) — unchanged call site for every project page. */
export const projectNav = (project: ProjectRailRef): readonly NavSection[] =>
  projectModuleSections(project)

/* ── My work module ──────────────────────────────────────────────────────── */

const MY_WORK_SECTION: NavSection = {
  headingKey: 'section.myWork',
  heading: 'My work',
  items: [
    link('item.myBoard', 'My board', '/my/board', SquareKanban),
    link('item.myProject', 'My project', '/my/project', FolderKanban),
    link('item.myTeam', 'My team', '/my/team', UsersRound),
    link('item.myClient', 'My client', '/my/client', Building2),
    // PX-W6.2: the org thread.
    link('item.messages', 'Messages', '/messages', MessageSquare),
    // PX-W6.4: the commercial spine's own-work surfaces.
    link('item.timesheet', 'Timesheet', '/my/timesheet', Clock),
    link('item.approvals', 'Approvals', '/my/approvals', CheckCheck),
  ],
}

const CLIENT_SECTION: NavSection = {
  headingKey: 'section.client',
  heading: 'Client',
  items: [link('item.clientPortal', 'Client portal', '/portal', PanelsTopLeft)],
}

/* ── Notifications module ────────────────────────────────────────────────── */

const NOTIFICATIONS_SECTION: NavSection = {
  headingKey: 'section.notifications',
  heading: 'Notifications',
  items: [link('item.notifications', 'Notifications', '/notifications', Bell)],
}

/* ── Administration module ───────────────────────────────────────────────── */

const ADMINISTRATION_SECTION: NavSection = {
  headingKey: 'section.administration',
  heading: 'Administration',
  items: [
    link('item.projectsAndPeople', 'Projects & people', '/admin/projects', ShieldCheck),
    // PX-W6.2: real administration surfaces.
    link('item.peopleAndAccess', 'People & access', '/admin/people', UsersRound),
    link('item.settings', 'Settings', '/admin/settings', Settings2),
    link('item.auditLog', 'Audit log', '/admin/audit', ScrollText),
    // PX-W6.4: the org's rate cards (admin-only destination).
    link('item.rateCards', 'Rate cards', '/admin/rates', Euro),
    // PX-W6.5: the configuration surfaces — real routes, no concept badge.
    link('item.modules', 'Modules', '/admin/modules', Boxes),
    link('item.connectors', 'Connectors', '/admin/connectors', Plug),
    link('item.runtime', 'Runtime', '/admin/runtime', Cpu),
    link('item.clientsAndModules', 'Clients & modules', '/admin/clients', Boxes),
    // Deck 0.3 — no declared route anywhere in the product; always coming-soon.
    unrouted('item.rolesAndPermissions', 'Roles & permissions', KeyRound),
  ],
}

/**
 * `myNav` and `adminNav` are each ONE module's own section(s) — no longer a
 * concatenation of Administration/My-work plus the whole Library and Resources
 * modules (that concatenation was the defect this ticket fixes). The `role`
 * parameter stays for call-site stability and because a genuinely role-shaped
 * per-module rail may be wanted later; what a role SEES is still decided by
 * `canAccess`, applied once, centrally, inside `AppShell` via `navForRole`.
 */
export const myNav = (_role: RoleKey): readonly NavSection[] => [MY_WORK_SECTION, CLIENT_SECTION]

export const adminNav = (_role: RoleKey): readonly NavSection[] => [ADMINISTRATION_SECTION]

/**
 * The PX group's own module rail (deck 0.1 Home / 0.2 Product map) —
 * VIS-C-01/02 (review cycle 2): HomePage and ArchitectureMapPage both belong
 * to the `home` module but neither passed a matching `nav` prop (HomePage
 * passed none at all; ArchitectureMapPage passed a stale `adminNav`), so the
 * PX section never rendered on either page.
 */
export const pxNav = (_role: RoleKey): readonly NavSection[] => [PX_SECTION]

/**
 * The Notifications module's own one-row rail — VIS-C-05 (review cycle 2):
 * NotificationsPage passed no `nav` prop, so its own section never rendered.
 */
export const notificationsNav = (_role: RoleKey): readonly NavSection[] => [NOTIFICATIONS_SECTION]

/**
 * The page sections the rail renders BELOW the level-1 switcher: every section
 * except a single-row one whose only item is the destination the switcher
 * already shows (VIS-C2-01 — PX and Notifications would otherwise repeat their
 * one row). `activeDestinationTo` is `undefined` on a path no destination owns
 * (`/designs`); the guard then keeps every section, so a lone unrouted item —
 * whose `to` is also `undefined` — is never mistaken for that duplicate
 * (TASK-505148A8, finding 8).
 */
export const sectionsBesideSwitcher = (
  sections: readonly NavSection[],
  activeDestinationTo: string | undefined
): readonly NavSection[] =>
  sections.filter(
    (section) =>
      !(
        activeDestinationTo !== undefined &&
        section.items.length === 1 &&
        section.items[0]?.to === activeDestinationTo
      )
  )

/* ── The level-1 model (TASK-33343000, spec §6.2 / §6.3) ────────────────── */

/** Stable identity for a module — i18n, tests and the Home cards key off this. */
export type NavModuleKey =
  | 'home'
  | 'resources'
  | 'library'
  | 'project'
  | 'my-work'
  | 'notifications'
  | 'admin'

export type NavGroupKey = 'px' | 'modules' | 'me' | 'admin'

export interface NavModule {
  key: NavModuleKey
  /** `module.<key>` in the `nav` namespace; `label` stays the EN literal (C-5). */
  labelKey: string
  label: string
  icon: LucideIcon
  /**
   * An i18n KEY (under `home.blurb.*`), resolved via `t()` at render time on
   * the Home card only — the nav model keeps the key, not the string (VIS-C-06
   * / code medium, review cycle 2). Since TASK-505148A8 the labels and headings
   * carry `nav` namespace keys too (`labelKey` / `headingKey`).
   */
  blurb: string
  sections: readonly NavSection[]
}

export interface NavGroup {
  key: NavGroupKey
  /** `group.<key>` in the `nav` namespace; `heading` stays the EN literal (C-5). */
  headingKey: string
  heading: string
  modules: readonly NavModule[]
}

/** A module resolved for one role: its openable entry point + its filtered rail. */
export interface NavDestination {
  key: NavModuleKey
  labelKey: string
  label: string
  icon: LucideIcon
  to: string
  sections: readonly NavSection[]
}

/**
 * One level-1 GROUP resolved for a role: the group's heading and the
 * destinations that role can actually reach. `navGroupsForRole` returns these
 * and the shell's switcher renders them, so the shape is declared once here
 * rather than spelled out structurally at each end.
 */
export interface NavGroupDestinations {
  key: NavGroupKey
  headingKey: string
  heading: string
  destinations: readonly NavDestination[]
}

const homeModule: NavModule = {
  key: 'home',
  labelKey: 'module.home',
  label: 'Home',
  icon: Home,
  blurb: 'home.blurb.home',
  sections: [PX_SECTION],
}

const resourcesModule: NavModule = {
  key: 'resources',
  labelKey: 'module.resources',
  label: 'Resources',
  icon: Users,
  blurb: 'home.blurb.resources',
  sections: resourcesNav,
}

const libraryModule: NavModule = {
  key: 'library',
  labelKey: 'module.library',
  label: 'Projects & library',
  icon: LayoutGrid,
  blurb: 'home.blurb.library',
  sections: libraryNav,
}

const projectModule = (project: ProjectRailRef | null): NavModule => ({
  key: 'project',
  labelKey: 'module.project',
  label: 'My projects',
  icon: FolderKanban,
  blurb: 'home.blurb.project',
  sections: projectModuleSections(project),
})

const myWorkModule: NavModule = {
  key: 'my-work',
  labelKey: 'module.my-work',
  label: 'My work',
  icon: SquareKanban,
  blurb: 'home.blurb.my-work',
  sections: [MY_WORK_SECTION, CLIENT_SECTION],
}

const notificationsModule: NavModule = {
  key: 'notifications',
  labelKey: 'module.notifications',
  label: 'Notifications',
  icon: Bell,
  blurb: 'home.blurb.notifications',
  sections: [NOTIFICATIONS_SECTION],
}

const adminModule: NavModule = {
  key: 'admin',
  labelKey: 'module.admin',
  label: 'Administration',
  icon: ShieldCheck,
  blurb: 'home.blurb.admin',
  sections: [ADMINISTRATION_SECTION],
}

/**
 * The whole level-1 model, in deck order (mockup 0.1). A project-scoped
 * module needs the ref to build real URLs; passing `null` (every call site in
 * this ticket — the shell has no project prop, and Home never does) still
 * resolves a real, openable "My projects" landing at `/project` (spec's own
 * deck-to-rail map: "a module's landing is its first built screen … /project
 * (resolves the first engagement)").
 */
export const navGroups = (project: ProjectRailRef | null): readonly NavGroup[] => [
  { key: 'px', headingKey: 'group.px', heading: 'PX', modules: [homeModule] },
  {
    key: 'modules',
    headingKey: 'group.modules',
    heading: 'MODULES',
    modules: [resourcesModule, libraryModule, projectModule(project)],
  },
  { key: 'me', headingKey: 'group.me', heading: 'ME', modules: [myWorkModule, notificationsModule] },
  { key: 'admin', headingKey: 'group.admin', heading: 'ADMIN', modules: [adminModule] },
]

/* ── The rail's two levels (CR-RAIL-DRILLDOWN-2026-09-26, spec R.6.1 / R.6.2) ─ */

/**
 * Which URL prefixes "belong to" a level-1 module, for the switcher's active
 * highlight (spec §6.5: "the ACTIVE destination is the one whose module owns
 * the current pathname"). A static map, not derived from a module's own
 * sections — `navGroups(null)` builds "My projects" with a placeholder id
 * when there is no concrete engagement in view, so its section `to`s are not
 * real URLs to match against.
 *
 * Moved here unchanged from `AppShell.tsx` (CR-RAIL-DRILLDOWN, spec R.6.1) so
 * the rail LEVEL — which reads the same table — is unit-tested beside the
 * model it describes.
 */
export const MODULE_PATH_PREFIXES: Readonly<Record<NavModuleKey, readonly string[]>> = {
  home: ['/home', '/architecture'],
  resources: ['/resources'],
  library: ['/projects', '/library', '/clients'],
  project: ['/project'],
  'my-work': ['/my', '/messages', '/portal'],
  notifications: ['/notifications'],
  admin: ['/admin'],
}

/** Exact prefix, or prefix + `/…` — so `/projects` is never owned by `project`. */
export const moduleOwnsPath = (key: NavModuleKey, path: string): boolean =>
  MODULE_PATH_PREFIXES[key].some((prefix) => path === prefix || path.startsWith(`${prefix}/`))

const MODULE_KEYS = Object.keys(MODULE_PATH_PREFIXES) as NavModuleKey[]

/** The single module owning `path`, or `null` when none does (`/`, `/designs`). */
export const moduleForPath = (path: string): NavModuleKey | null =>
  MODULE_KEYS.find((key) => moduleOwnsPath(key, path)) ?? null

/**
 * The modules that drill down to a level-2 rail. Decision R-3: PX/Home and
 * Notifications have no rows beyond their own destination, so they stay on
 * level 1 (a back row over one row would be furniture).
 */
export type DrillModuleKey = Exclude<NavModuleKey, 'home' | 'notifications'>

/**
 * The level a path renders the rail at (spec I-13, without the back-row
 * toggle, which lives in the rail component). Total and pure: no role, store
 * or router input — a deep link, a reload and a history pop all land here.
 */
export type RailLevel =
  | { level: 1; module: NavModuleKey | null }
  | { level: 2; module: DrillModuleKey }

const isDrillModule = (key: NavModuleKey): key is DrillModuleKey =>
  key !== 'home' && key !== 'notifications'

export const railLevelFor = (path: string): RailLevel => {
  const module = moduleForPath(path)
  return module !== null && isDrillModule(module) ? { level: 2, module } : { level: 1, module }
}

/**
 * The pinned "Me" rows under a level-2 rail (spec R.6.2): the role's `me`
 * destinations in model order (My work, Notifications), minus
 *  - the destination of the module the user is in (Decision R-2 — inside My
 *    work the pinned My work row would repeat the module's own first row), and
 *  - any destination whose `to` a rendered section row already links to
 *    (I-15, the level-2 half of the VIS-C2-01 duplicate guard; the level-1
 *    half stays `sectionsBesideSwitcher`).
 *
 * `groups` is `navGroupsForRole` output, so the role filter stays the only
 * visibility gate (I-14); `[]` when the role has no `me` group.
 */
export const pinnedMeDestinations = (
  groups: readonly NavGroupDestinations[],
  active: DrillModuleKey,
  sections: readonly NavSection[]
): readonly NavDestination[] => {
  const shown = new Set(
    sections.flatMap((section) => section.items).flatMap((item) => (item.to === undefined ? [] : [item.to]))
  )
  const { destinations = [] } = groups.find((group) => group.key === 'me') ?? {}
  return destinations.filter((destination) => destination.key !== active && !shown.has(destination.to))
}

/**
 * The sections a level-2 rail renders (spec I-17): the page's own rail when it
 * passes one (the caller has already run it through `navForRole`); otherwise
 * the role's destination sections for the active module, so a page with no
 * `nav` prop (`ClientPortalPage`) still shows its module's rows; never for My
 * projects, whose model rows need an engagement id; `[]` when the role has no
 * destination in the module (the `RoleBlockedPage` shape).
 */
export const levelTwoSections = (
  pageNav: readonly NavSection[] | undefined,
  destination: NavDestination | undefined,
  module: DrillModuleKey
): readonly NavSection[] => pageNav ?? (module === 'project' ? [] : (destination?.sections ?? []))
